import React, { useEffect, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import Icon from '../common/Icon';
import { callLogService } from '../../services/callLogService';
import { CallLog } from '../../types';
import { formatDateTime } from '../../utils/dateUtils';

interface PriorCallAlertProps {
  entityType: 'lead' | 'customer' | 'tender';
  entityId: number;
  /** Exclude in-progress call log (e.g. dialer session just started). */
  excludeCallLogId?: number;
  className?: string;
}

const PriorCallAlert: React.FC<PriorCallAlertProps> = ({
  entityType,
  entityId,
  excludeCallLogId,
  className = '',
}) => {
  const [priorCall, setPriorCall] = useState<CallLog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    callLogService
      .getLatestByEntity(entityType, entityId, excludeCallLogId ? { exclude_id: excludeCallLogId } : undefined)
      .then((data) => {
        if (!cancelled) setPriorCall(data);
      })
      .catch(() => {
        if (!cancelled) setPriorCall(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, excludeCallLogId]);

  if (loading || !priorCall) {
    return null;
  }

  const callerName = priorCall.called_by_name || 'Another executive';
  const when = formatDateTime(priorCall.call_date, priorCall.call_time);

  return (
    <div
      className={`mb-4 flex gap-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-amber-950 ${className}`}
      role="alert"
    >
      <Icon component={FiAlertTriangle} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
      <div className="text-sm">
        <p className="font-semibold">This contact was already called</p>
        <p className="mt-1 text-amber-900">
          <span className="font-medium">{callerName}</span> called on{' '}
          <span className="font-medium">{when}</span>
          {priorCall.call_status ? (
            <>
              {' '}
              — status: <span className="font-medium">{priorCall.call_status}</span>
            </>
          ) : null}
          {priorCall.call_outcome ? (
            <>
              , outcome: <span className="font-medium">{priorCall.call_outcome}</span>
            </>
          ) : null}
          .
        </p>
        <p className="mt-1 text-xs text-amber-800">
          Please review before calling again to avoid repeat calls and customer dissatisfaction.
        </p>
      </div>
    </div>
  );
};

export default PriorCallAlert;
